import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useEditor } from '@tiptap/react';
import axios from 'axios';
import useScrollLock from '../../hooks/useScrollLock';
import { useLanguage } from '../../contexts/LanguageContext';
import { getImageUrl } from '../../utils/api';
import TemplateEditorWorkspace, { TEMPLATE_EDITOR_EXTENSIONS } from './TemplateEditorWorkspace';
import PlaceholdersSidebar from './PlaceholdersSidebar';
import EditableModalTitle from './EditableModalTitle';
import ErrorModal from './ErrorModal';
import EditorOptionsPanel from './EditorOptionsPanel';
import {
  DEFAULT_TEMPLATE_FONT_SIZE,
  wrapTemplateContent,
  parseTemplateContent
} from '../../utils/templateEditorFontSize';
import {
  DEFAULT_PAGE_WIDTH_CM,
  DEFAULT_PAGE_HEIGHT_CM,
  detectDefaultLengthUnit,
  formatLengthForInput,
  parseLengthToCm
} from '../../utils/lengthUnit';
import { useSalon } from '../../contexts/SalonContext';
import './Salon.css';

const DECLARATION_PLACEHOLDER_GROUPS = ['general', 'product', 'care', 'meta'];

const CreateDeclarationTemplateModal = ({ isOpen, onClose, salonId, onTemplateCreated, editingDeclaration }) => {
  const { t } = useLanguage();
  const { salonSettings } = useSalon();
  const lengthUnit = salonSettings?.length_unit?.value === 'in' || salonSettings?.length_unit?.value === 'cm'
    ? salonSettings.length_unit.value
    : detectDefaultLengthUnit();

  const [formData, setFormData] = useState({
    title: '',
    is_active: true
  });
  const [loading, setLoading] = useState(false);

  // Lock body scroll when modal is open
  useScrollLock(isOpen);
  const [error, setError] = useState('');
  const [placeholders, setPlaceholders] = useState([]);
  const [loadingPlaceholders, setLoadingPlaceholders] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [originalContent, setOriginalContent] = useState('');
  const [originalFontSize, setOriginalFontSize] = useState(DEFAULT_TEMPLATE_FONT_SIZE);
  const [fontSize, setFontSize] = useState(DEFAULT_TEMPLATE_FONT_SIZE);
  const [pageWidthCm, setPageWidthCm] = useState(DEFAULT_PAGE_WIDTH_CM);
  const [pageHeightCm, setPageHeightCm] = useState(DEFAULT_PAGE_HEIGHT_CM);
  const [pageWidthInput, setPageWidthInput] = useState(
    () => formatLengthForInput(DEFAULT_PAGE_WIDTH_CM, detectDefaultLengthUnit())
  );
  const [pageHeightInput, setPageHeightInput] = useState(
    () => formatLengthForInput(DEFAULT_PAGE_HEIGHT_CM, detectDefaultLengthUnit())
  );
  const [originalPageWidthCm, setOriginalPageWidthCm] = useState(DEFAULT_PAGE_WIDTH_CM);
  const [originalPageHeightCm, setOriginalPageHeightCm] = useState(DEFAULT_PAGE_HEIGHT_CM);
  const [isEditorEmpty, setIsEditorEmpty] = useState(true);
  const originalContentRef = useRef('');
  const originalFontSizeRef = useRef(DEFAULT_TEMPLATE_FONT_SIZE);
  const fontSizeRef = useRef(DEFAULT_TEMPLATE_FONT_SIZE);

  useEffect(() => {
    originalContentRef.current = originalContent;
  }, [originalContent]);

  useEffect(() => {
    originalFontSizeRef.current = originalFontSize;
  }, [originalFontSize]);

  useEffect(() => {
    fontSizeRef.current = fontSize;
  }, [fontSize]);

  const editor = useEditor({
    // Avoid accessing the view before EditorContent mounts (Firefox throws on editor.view)
    immediatelyRender: false,
    // Since TipTap 2.7/@tiptap/react v3, useEditor() no longer re-renders the component on every
    // transaction by default (a perf change) - without this, every isActive()/getAttributes()
    // check in the toolbar (bold, headings, alignment, image overlay, all of it) reads stale
    // state and never visibly updates as the cursor/selection moves.
    shouldRerenderOnTransaction: true,
    extensions: TEMPLATE_EDITOR_EXTENSIONS,
    content: '',
    // Without this, ProseMirror's HTML parser collapses runs of spaces down to one
    // (both on initial load and while typing) - this preserves them as typed.
    parseOptions: { preserveWhitespace: 'full' },
    editorProps: {
      attributes: {
        class: 'contract-editor-content',
      },
    },
    onUpdate: ({ editor }) => {
      // Check if content has changed
      const currentContent = editor.getHTML();
      setHasUnsavedChanges(
        currentContent !== originalContentRef.current
        || fontSizeRef.current !== originalFontSizeRef.current
      );

      // Check if editor is empty (check for actual text content)
      const textContent = editor.getText().trim();
      const isEmpty = textContent === '';
      setIsEditorEmpty(isEmpty);
    },
  });

  // Fetch available placeholders
  const fetchPlaceholders = useCallback(async () => {
    try {
      setLoadingPlaceholders(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/salons/${salonId}/declarations/placeholders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlaceholders(response.data.placeholders || []);
    } catch (error) {
      console.error('Error fetching placeholders:', error);
    } finally {
      setLoadingPlaceholders(false);
    }
  }, [salonId]);

  // Load placeholders when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchPlaceholders();
    }
  }, [isOpen, fetchPlaceholders]);

  // Check initial editor state
  useEffect(() => {
    if (editor) {
      const textContent = editor.getText().trim();
      setIsEditorEmpty(textContent === '');
    }
  }, [editor]);

  // Populate form when editing
  useEffect(() => {
    if (!isOpen || !editor || editor.isDestroyed) return;

    if (editingDeclaration) {
      setFormData({
        title: editingDeclaration.title || '',
        is_active: editingDeclaration.is_active !== undefined ? editingDeclaration.is_active : true
      });

      // Set editor content
      const { fontSize: parsedFontSize, html } = parseTemplateContent(
        editingDeclaration.declaration_content || ''
      );
      editor.commands.setContent(html, { parseOptions: { preserveWhitespace: 'full' } });
      setFontSize(parsedFontSize);
      setOriginalFontSize(parsedFontSize);
      setOriginalContent(html);
      const widthCm = Number(editingDeclaration.page_width) || DEFAULT_PAGE_WIDTH_CM;
      const heightCm = Number(editingDeclaration.page_height) || DEFAULT_PAGE_HEIGHT_CM;
      setPageWidthCm(widthCm);
      setPageHeightCm(heightCm);
      setPageWidthInput(formatLengthForInput(widthCm, lengthUnit));
      setPageHeightInput(formatLengthForInput(heightCm, lengthUnit));
      setOriginalPageWidthCm(widthCm);
      setOriginalPageHeightCm(heightCm);
      setHasUnsavedChanges(false);

      // Update empty state
      const textContent = editor.getText().trim();
      setIsEditorEmpty(textContent === '');
    } else {
      // Reset form for new declaration
      setFormData({
        title: '',
        is_active: true
      });
      // Reset editor content to empty
      editor.commands.setContent('');
      setOriginalContent('');
      setFontSize(DEFAULT_TEMPLATE_FONT_SIZE);
      setOriginalFontSize(DEFAULT_TEMPLATE_FONT_SIZE);
      setPageWidthCm(DEFAULT_PAGE_WIDTH_CM);
      setPageHeightCm(DEFAULT_PAGE_HEIGHT_CM);
      setPageWidthInput(formatLengthForInput(DEFAULT_PAGE_WIDTH_CM, lengthUnit));
      setPageHeightInput(formatLengthForInput(DEFAULT_PAGE_HEIGHT_CM, lengthUnit));
      setOriginalPageWidthCm(DEFAULT_PAGE_WIDTH_CM);
      setOriginalPageHeightCm(DEFAULT_PAGE_HEIGHT_CM);
      setHasUnsavedChanges(false);

      // Update empty state
      setIsEditorEmpty(true);
    }
  }, [isOpen, editingDeclaration, editor]); // eslint-disable-line react-hooks/exhaustive-deps -- lengthUnit intentionally excluded: this populates/resets the whole form on open, and must not re-run (discarding in-progress edits) if the unit preference changes while open

  useEffect(() => {
    setPageWidthInput(formatLengthForInput(pageWidthCm, lengthUnit));
    setPageHeightInput(formatLengthForInput(pageHeightCm, lengthUnit));
  }, [lengthUnit]); // eslint-disable-line react-hooks/exhaustive-deps -- only reformat when unit preference changes

  const handleFontSizeChange = (next) => {
    setFontSize(next);
    setHasUnsavedChanges(
      (editor?.getHTML() || '') !== originalContent
      || next !== originalFontSize
      || pageWidthCm !== originalPageWidthCm
      || pageHeightCm !== originalPageHeightCm
    );
  };

  const handlePageDimensionChange = (dimension, rawValue) => {
    if (dimension === 'width') setPageWidthInput(rawValue);
    else setPageHeightInput(rawValue);

    if (rawValue === '' || rawValue === '.' || rawValue === '-') return;

    const cm = parseLengthToCm(rawValue, lengthUnit);
    if (!Number.isFinite(cm)) return;
    const nextWidth = dimension === 'width' ? cm : pageWidthCm;
    const nextHeight = dimension === 'height' ? cm : pageHeightCm;
    if (dimension === 'width') setPageWidthCm(cm);
    else setPageHeightCm(cm);
    setHasUnsavedChanges(
      (editor?.getHTML() || '') !== originalContent
      || fontSize !== originalFontSize
      || nextWidth !== originalPageWidthCm
      || nextHeight !== originalPageHeightCm
    );
  };

  const buildDeclarationPayload = () => ({
    title: formData.title,
    declaration_content: wrapTemplateContent(editor.getHTML(), fontSize),
    is_active: formData.is_active,
    page_width: Math.round(pageWidthCm * 100) / 100,
    page_height: Math.round(pageHeightCm * 100) / 100
  });

  const handleTitleChange = (title) => {
    setFormData(prev => ({ ...prev, title }));
    setHasUnsavedChanges(true);
  };

  const handleActiveChange = (e) => {
    setFormData(prev => ({ ...prev, is_active: e.target.checked }));
    setHasUnsavedChanges(true);
  };

  const insertPlaceholder = (placeholder) => {
    if (editor) {
      editor.commands.insertContent(`{{${placeholder}}}`);
      editor.commands.focus();
    }
  };

  const uploadImage = async (file) => {
    const token = localStorage.getItem('token');
    const formDataPayload = new FormData();
    formDataPayload.append('image', file);
    const response = await axios.post(`/api/salons/${salonId}/declarations/upload-image`, formDataPayload, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
    });
    return getImageUrl(response.data.url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      setError(t('settings.titleRequired'));
      return;
    }

    if (!editor.getHTML() || editor.getHTML() === '<p></p>') {
      setError(t('settings.contentRequired'));
      return;
    }

    if (
      !Number.isFinite(pageWidthCm) || !Number.isFinite(pageHeightCm) ||
      pageWidthCm < 0.5 || pageWidthCm > 100 ||
      pageHeightCm < 0.5 || pageHeightCm > 100
    ) {
      setError(t('settings.pageSizeInvalid'));
      return;
    }

    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const declarationData = buildDeclarationPayload();

      let response;
      if (editingDeclaration) {
        response = await axios.put(`/api/salons/${salonId}/declarations/${editingDeclaration.id}`, declarationData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(`/api/salons/${salonId}/declarations`, declarationData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (response.status === 200 || response.status === 201) {
        const currentContent = editor.getHTML();
        setOriginalContent(currentContent);
        setOriginalFontSize(fontSize);
        setOriginalPageWidthCm(pageWidthCm);
        setOriginalPageHeightCm(pageHeightCm);
        setHasUnsavedChanges(false);
        onTemplateCreated();

        if (!editingDeclaration || showUnsavedWarning) {
          handleClose();
        }
      }
    } catch (error) {
      console.error('Error saving declaration template:', error);
      setError(error.response?.data?.error || `Failed to ${editingDeclaration ? 'update' : 'create'} declaration template`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      return;
    }

    // Reset form state
    setFormData({
      title: '',
      is_active: true
    });
    setError('');
    setHasUnsavedChanges(false);
    setShowUnsavedWarning(false);
    if (editor) {
      editor.commands.setContent('');
    }
    setFontSize(DEFAULT_TEMPLATE_FONT_SIZE);
    setOriginalFontSize(DEFAULT_TEMPLATE_FONT_SIZE);
    setPageWidthCm(DEFAULT_PAGE_WIDTH_CM);
    setPageHeightCm(DEFAULT_PAGE_HEIGHT_CM);
    setPageWidthInput(formatLengthForInput(DEFAULT_PAGE_WIDTH_CM, lengthUnit));
    setPageHeightInput(formatLengthForInput(DEFAULT_PAGE_HEIGHT_CM, lengthUnit));
    setOriginalPageWidthCm(DEFAULT_PAGE_WIDTH_CM);
    setOriginalPageHeightCm(DEFAULT_PAGE_HEIGHT_CM);
    onClose();
  };

  const handleDiscardChanges = () => {
    setFormData({
      title: '',
      is_active: true
    });
    setError('');
    setHasUnsavedChanges(false);
    setShowUnsavedWarning(false);
    if (editor) {
      editor.commands.setContent('');
    }
    setFontSize(DEFAULT_TEMPLATE_FONT_SIZE);
    setOriginalFontSize(DEFAULT_TEMPLATE_FONT_SIZE);
    setPageWidthCm(DEFAULT_PAGE_WIDTH_CM);
    setPageHeightCm(DEFAULT_PAGE_HEIGHT_CM);
    setPageWidthInput(formatLengthForInput(DEFAULT_PAGE_WIDTH_CM, lengthUnit));
    setPageHeightInput(formatLengthForInput(DEFAULT_PAGE_HEIGHT_CM, lengthUnit));
    setOriginalPageWidthCm(DEFAULT_PAGE_WIDTH_CM);
    setOriginalPageHeightCm(DEFAULT_PAGE_HEIGHT_CM);
    onClose();
  };

  const handleSaveAndClose = async () => {
    // Validate required fields first
    if (!formData.title.trim()) {
      setError(t('settings.titleRequired'));
      return;
    }

    if (!editor.getHTML() || editor.getHTML() === '<p></p>') {
      setError(t('settings.contentRequired'));
      return;
    }

    if (
      !Number.isFinite(pageWidthCm) || !Number.isFinite(pageHeightCm) ||
      pageWidthCm < 0.5 || pageWidthCm > 100 ||
      pageHeightCm < 0.5 || pageHeightCm > 100
    ) {
      setError(t('settings.pageSizeInvalid'));
      return;
    }

    try {
      setLoading(true);
      setError('');

      const token = localStorage.getItem('token');
      const declarationData = buildDeclarationPayload();

      let response;
      if (editingDeclaration) {
        response = await axios.put(`/api/salons/${salonId}/declarations/${editingDeclaration.id}`, declarationData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        response = await axios.post(`/api/salons/${salonId}/declarations`, declarationData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      if (response.status === 200 || response.status === 201) {
        const currentContent = editor.getHTML();
        setOriginalContent(currentContent);
        setOriginalFontSize(fontSize);
        setOriginalPageWidthCm(pageWidthCm);
        setOriginalPageHeightCm(pageHeightCm);
        setHasUnsavedChanges(false);
        setShowUnsavedWarning(false);
        onTemplateCreated();

        setFormData({
          title: '',
          is_active: true
        });
        setError('');
        if (editor) {
          editor.commands.setContent('');
        }
        setFontSize(DEFAULT_TEMPLATE_FONT_SIZE);
        setOriginalFontSize(DEFAULT_TEMPLATE_FONT_SIZE);
        setPageWidthCm(DEFAULT_PAGE_WIDTH_CM);
        setPageHeightCm(DEFAULT_PAGE_HEIGHT_CM);
        setPageWidthInput(formatLengthForInput(DEFAULT_PAGE_WIDTH_CM, lengthUnit));
        setPageHeightInput(formatLengthForInput(DEFAULT_PAGE_HEIGHT_CM, lengthUnit));
        setOriginalPageWidthCm(DEFAULT_PAGE_WIDTH_CM);
        setOriginalPageHeightCm(DEFAULT_PAGE_HEIGHT_CM);
        onClose();
      }
    } catch (error) {
      console.error('Error saving declaration template:', error);
      setError(error.response?.data?.error || `Failed to ${editingDeclaration ? 'update' : 'create'} declaration template`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelWarning = () => {
    setShowUnsavedWarning(false);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal contract-template-modal">
        <div className="modal-header">
          <EditableModalTitle
            value={formData.title}
            onChange={handleTitleChange}
            placeholder={t('settings.enterTemplateTitle')}
            editLabel={t('settings.editTitle')}
          />
          <div className="modal-header-right">
            <label className="checkbox-label modal-header-active-toggle">
              <input type="checkbox" checked={formData.is_active} onChange={handleActiveChange} />
              <span className="checkmark"></span>
              {t('settings.active')}
            </label>
            <button className="modal-close" onClick={handleClose}>
              ×
            </button>
          </div>
        </div>

        <div className="contract-modal-content">
          {/* Placeholders Sidebar */}
          <PlaceholdersSidebar description={t('settings.clickToInsertDeclaration')}>
            {loadingPlaceholders ? (
              <div className="loading-spinner-small"></div>
            ) : placeholders.length === 0 ? (
              <div className="no-placeholders">
                <p>{t('settings.noPlaceholdersAvailable')}</p>
              </div>
            ) : (
              <div className="placeholders-list">
                {DECLARATION_PLACEHOLDER_GROUPS.map((group) => {
                  const groupPlaceholders = placeholders.filter((p) => p.group === group);
                  if (groupPlaceholders.length === 0) return null;
                  return (
                    <div key={group} className="placeholder-group">
                      <h5>{t(`settings.declarationGroup${group.charAt(0).toUpperCase()}${group.slice(1)}`, group)}</h5>
                      {groupPlaceholders.map((placeholder) => {
                        const translatedDescription = t(`settings.placeholders.${placeholder.key}`, placeholder.description);
                        return (
                          <button
                            key={placeholder.key}
                            type="button"
                            className="placeholder-button"
                            onClick={() => insertPlaceholder(placeholder.key)}
                            title={translatedDescription}
                          >
                            <span className="placeholder-key">{`{{${placeholder.key}}}`}</span>
                            <span className="placeholder-description">{translatedDescription}</span>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </PlaceholdersSidebar>

          {/* Main Form Content */}
          <div className="contract-form-content">
            <form onSubmit={handleSubmit} className="base-form">
              <EditorOptionsPanel>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="page_width">
                      {t('settings.pageWidth')} ({lengthUnit === 'in' ? t('settings.inchesShort') : t('settings.centimetersShort')})
                    </label>
                    <input
                      type="number"
                      id="page_width"
                      min={lengthUnit === 'in' ? 0.2 : 0.5}
                      max={lengthUnit === 'in' ? 39 : 100}
                      step={lengthUnit === 'in' ? 0.01 : 0.1}
                      value={pageWidthInput}
                      onChange={(e) => handlePageDimensionChange('width', e.target.value)}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="page_height">
                      {t('settings.pageHeight')} ({lengthUnit === 'in' ? t('settings.inchesShort') : t('settings.centimetersShort')})
                    </label>
                    <input
                      type="number"
                      id="page_height"
                      min={lengthUnit === 'in' ? 0.2 : 0.5}
                      max={lengthUnit === 'in' ? 39 : 100}
                      step={lengthUnit === 'in' ? 0.01 : 0.1}
                      value={pageHeightInput}
                      onChange={(e) => handlePageDimensionChange('height', e.target.value)}
                    />
                    <small className="form-help-text">{t('settings.pageSizeHint')}</small>
                  </div>
                </div>
              </EditorOptionsPanel>

              <div className="form-row editor-row">
                <div className="form-group">
                  <TemplateEditorWorkspace
                    editor={editor}
                    fontSize={fontSize}
                    onFontSizeChange={handleFontSizeChange}
                    isEditorEmpty={isEditorEmpty}
                    onUploadImage={uploadImage}
                    onUploadError={setError}
                  />
                </div>
              </div>

            </form>
          </div>
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="salon-button secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="salon-button primary"
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading ? (editingDeclaration ? t('settings.updating') : t('settings.creating')) : (editingDeclaration ? t('settings.updateTemplate') : t('settings.createTemplate'))}
          </button>
        </div>
      </div>

      {/* Unsaved Changes Warning Modal */}
      {showUnsavedWarning && (
        <div className="modal-overlay" style={{ zIndex: 1001 }}>
          <div className="modal-content" style={{
            maxWidth: '450px',
            width: '90%',
            margin: '0 auto'
          }}>
            <div className="modal-header" style={{
              padding: '20px 24px 16px 24px',
              borderBottom: '1px solid #e5e7eb'
            }}>
              <h3 style={{
                margin: '0',
                fontSize: '18px',
                fontWeight: '600',
                color: '#374151'
              }}>{t('settings.unsavedChanges')}</h3>
            </div>
            <div className="modal-body" style={{
              padding: '20px 24px',
              lineHeight: '1.5'
            }}>
              <p style={{
                margin: '0',
                fontSize: '14px',
                color: '#6b7280'
              }}>{t('settings.unsavedChangesMessage')}</p>
            </div>
            <div className="modal-footer" style={{
              padding: '16px 24px 20px 24px',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                type="button"
                className="salon-button secondary"
                onClick={handleCancelWarning}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px'
                }}
              >
                {t('settings.continueEditing')}
              </button>
              <button
                type="button"
                className="salon-button danger"
                onClick={handleDiscardChanges}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px'
                }}
              >
                {t('settings.discardChanges')}
              </button>
              <button
                type="button"
                className="salon-button primary"
                onClick={handleSaveAndClose}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  fontSize: '14px'
                }}
              >
                {loading ? t('settings.saving') : t('settings.saveAndClose')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ErrorModal message={error} onClose={() => setError('')} />
    </div>
  );
};

export default CreateDeclarationTemplateModal;
